import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSearch} from "@fortawesome/free-solid-svg-icons";

// Components
import Help from "./Help.js";

// Style
import {z} from "../style/components/mixins.js";
import {border, pad, radius} from "../style/components/variables.js";
import {
  List,
  ListItem,
  Label,
  scrollbar,
  Search,
  SearchIcon,
  SearchWrapper,
  Button,
  Text
} from "../style/components/general.js";

const SearchSelect = ({
  testId = "search-select",
  label,
  help,
  search,
  results = [],
  setResults,
  showMore = false,
  add,
  allowNew = false,
  placeholder,
  maxWidth = 500,
  large = false,
  showAll = false,
  keepOpen = false
}) => {
  const [focus, setFocus] = useState(false);
  const [overflowStates, setOverflowStates] = useState([]);

  const outside = useRef();
  const searchInput = useRef();
  const resultElement = useRef();
  const resultRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        outside?.current &&
        !outside.current.contains(e.target) &&
        !outside.current.contains(e.target.nextSibling)
      ) {
        setResults([]);
        setFocus(false);
        searchInput.current.blur();
      }
    };

    if (outside && focus && keepOpen) document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [focus, keepOpen, setResults]);

  const handleSearch = ({target}) => search(target.value);

  const resultsWidth = useMemo(() => maxWidth - 20, [maxWidth]);

  const evaluateWidth = useCallback(() => {
    const updatedOverflowStates = Object.fromEntries(
      Object.entries(resultRefs.current).map(([key, width]) => [key, width > resultsWidth])
    );

    setOverflowStates(updatedOverflowStates);
  }, [resultsWidth]);

  // TODO: add back for initial results to get truncated, but fix overflow error
  // useLayoutEffect(() => {
  //   if (results) evaluateWidth();
  // }, [results, evaluateWidth]);

  const middleTruncate = str => {
    const keep = str.length - 20;
    const start = Math.ceil(keep / 2);
    const end = Math.floor(keep / 2);
    return `${str.slice(0, start)}...${str.slice(str.length - end)}`;
  };

  return (
    <div>
      {label && <Label bold>{label.toUpperCase()}</Label>}&nbsp;{help && <Help>{help}</Help>}
      <SearchWrapper ref={outside} data-testid={testId}>
        <SearchIcon>
          <FontAwesomeIcon icon={faSearch} />
        </SearchIcon>
        <Search
          ref={searchInput}
          name="search"
          key="fields"
          type="text"
          data-testid={`${testId}-search`}
          placeholder={placeholder ?? "Find field..."}
          onChange={e => {
            evaluateWidth();

            handleSearch(e);
          }}
          onBlur={() => {
            if (!keepOpen)
              setTimeout(() => {
                setFocus(false);
                setResults([]);
              }, 150);
          }}
          onFocus={() => {
            evaluateWidth();

            setFocus(true);
            search(searchInput.current.value || "");
          }}
          autoComplete="off"
        />
        {(results?.length > 0 || allowNew) &&
        focus &&
        (searchInput.current?.value.length || showAll) ? (
          <Results
            ref={resultElement}
            hideScroll={results?.length === 1}
            large={large}
            showMore={showMore}
            overrideWidth={resultsWidth}>
            {results?.map(result => {
              const target =
                result.optionLabel ||
                (result.firstName && result.lastName
                  ? `${result.firstName} ${result.lastName}`
                  : result.label || result.name || result);
              const formatted = overflowStates[target] ? middleTruncate(target) : target;
              return (
                <Result
                  data-testid={`${testId}-result-${
                    result.value ||
                    result.name ||
                    result.id ||
                    result.publicId ||
                    result.email ||
                    result
                  }`}
                  key={`result-${
                    result.value ||
                    result.name ||
                    result.id ||
                    result.publicId ||
                    result.email ||
                    result
                  }`}
                  onClick={() => {
                    add(result);
                    searchInput.current.value = "";
                    setFocus(false);
                    if (!keepOpen) setResults([]);
                    else if (results) {
                      const selected =
                        result.value || result.name || result.id || result.email || result;
                      setResults(
                        results.filter(r => {
                          const rValue = r.value || r.name || r.id || r.email || r;
                          return rValue !== selected;
                        })
                      );
                    }
                  }}>
                  <ResultLabel
                    ref={el => {
                      if (el && !resultRefs.current[target]) resultRefs.current[target] = el.scrollWidth;
                    }}>
                    {formatted}
                  </ResultLabel>
                  {result.icon && (
                    <>
                      &nbsp;&nbsp;
                      <IconWrapper color={`#${result.color}`}>
                        <FontAwesomeIcon icon={result.icon} />
                      </IconWrapper>
                      &nbsp;&nbsp;
                    </>
                  )}
                </Result>
              );
            })}
            {allowNew && (
              <Result>
                <Button
                  type="button"
                  onClick={() => {
                    if (searchInput.current && searchInput.current.value.length > 0) {
                      add(searchInput.current.value);
                      searchInput.current.value = "";
                    }
                  }}>
                  Add New...
                </Button>
              </Result>
            )}
          </Results>
        ) : (
          ""
        )}
      </SearchWrapper>
    </div>
  );
};

SearchSelect.propTypes = {
  testId: PropTypes.string,
  label: PropTypes.string,
  help: PropTypes.string,
  search: PropTypes.func.isRequired,
  results: PropTypes.array,
  setResults: PropTypes.func.isRequired,
  add: PropTypes.func.isRequired,
  allowNew: PropTypes.bool,
  placeholder: PropTypes.string,
  showMore: PropTypes.bool,
  maxWidth: PropTypes.number,
  large: PropTypes.bool,
  showAll: PropTypes.bool,
  keepOpen: PropTypes.bool
};

// Style Overrides
const Results = styled(List)`
  position: absolute;
  top: 100%;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: start;
  width: ${({overrideWidth}) => (overrideWidth ? `${overrideWidth}px` : "min-content")};
  min-width: ${({large}) => (large ? "100%" : "155px")};
  max-height: ${({showMore}) => (showMore ? 125 : 90)}px;
  margin-top: ${pad / 4}px;
  background: ${({theme}) => theme.tertiary};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  box-shadow: 0 3px 5px rgb(0, 0, 0, 0.5);
  z-index: ${z("top")};

  ${({hideScroll}) =>
    hideScroll
      ? css`
          overflow-y: hidden;
        `
      : css`
          overflow-y: auto;
          ${scrollbar};
        `}
`;

const Result = styled(ListItem)`
  display: inline-block;
  width: 100%;
  padding: 0 ${pad}px;
  white-space: nowrap;
  border: ${border} solid ${({theme}) => theme.tertiary};

  &:hover {
    color: ${({theme}) => theme.tertiary};
    background: ${({theme}) => theme.secondary};
    border: ${border} solid ${({theme}) => theme.secondary};

    ${Button}, ${Text} {
      color: ${({theme}) => theme.tertiary};
    }
  }

  ${Button} {
    width: 100%;
    text-align: left;
    padding: 0;
    background: 0;
    color: ${({theme}) => theme.secondary};
  }
`;

const ResultLabel = styled(Text)`
  width: min-content;
`;

const IconWrapper = styled.span`
  ${({color}) =>
    color &&
    css`
      svg {
        fill: ${color};

        path {
          stroke: #000000;
          stroke-width: 10;
        }
      }
    `}
`;

export default SearchSelect;
